import { DownloadOutlined } from '@ant-design/icons'
import { Button, message, Modal, Progress } from 'antd'
import { UserMeyRealSearchParams, UserMeyRealType } from 'Modules'
import { useState } from 'react'
import * as XLSX from 'xlsx'

import { AppAdmin } from 'api/app-admin'
import { USER_MEY_REAL_KYC_STATUS_MAP, USER_MEY_REAL_STATUS_MAP } from 'constants/user-mey-real'
import { logError } from 'utils/logger'

interface ButtonExportUserMeyRealProps {
  searchParams: UserMeyRealSearchParams
}

const PAGE_SIZE = 100

const ButtonExportUserMeyReal = ({ searchParams }: ButtonExportUserMeyRealProps) => {
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)

  const exportToExcel = (data: UserMeyRealType[]) => {
    const exportData = data.map((item, index) => ({
      STT: index + 1,
      'Wallet Address': item.walletAddress,
      Email: item.email || '',
      'KYC Status': USER_MEY_REAL_KYC_STATUS_MAP[item.kycStatus]?.label || 'Not Submitted',
      'PTO Purchased': item.ptoPurchased,
      Status: USER_MEY_REAL_STATUS_MAP[item.status]?.label || 'Unknown',
      'Created At': item.createdAt ? new Date(item.createdAt).toLocaleString() : '',
      'Updated At': item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '',
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users')

    // Auto-fit column widths
    const colWidths = [
      { wch: 5 }, // STT
      { wch: 45 }, // Wallet Address
      { wch: 30 }, // Email
      { wch: 15 }, // KYC Status
      { wch: 15 }, // PTO Purchased
      { wch: 10 }, // Status
      { wch: 20 }, // Created At
      { wch: 20 }, // Updated At
    ]
    worksheet['!cols'] = colWidths

    const fileName = `user_mey_real_${new Date().toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
  }

  const handleExport = async () => {
    setLoading(true)
    setProgress(0)

    try {
      // First call to get total
      const firstRes = await AppAdmin.getUserMeyRealList({
        ...searchParams,
        page: 1,
        limit: PAGE_SIZE,
      })

      if (!firstRes?.data?.data) {
        message.error('Failed to fetch data')
        return
      }

      const totalResults = firstRes.data.data.totalResults || 0
      const totalPages = Math.ceil(totalResults / PAGE_SIZE)

      if (totalResults === 0) {
        message.warning('No data to export')
        return
      }

      let allData: UserMeyRealType[] = [...(firstRes.data.data.results || [])]
      setProgress(Math.round((1 / totalPages) * 100))

      // Fetch remaining pages
      for (let page = 2; page <= totalPages; page++) {
        const res = await AppAdmin.getUserMeyRealList({
          ...searchParams,
          page,
          limit: PAGE_SIZE,
        })

        if (res?.data?.data?.results) {
          allData = [...allData, ...res.data.data.results]
        }

        setProgress(Math.round((page / totalPages) * 100))
      }

      exportToExcel(allData)
      message.success(`Exported ${allData.length} records successfully`)
    } catch (error) {
      logError('Export error:', error)
      message.error('Failed to export data')
    } finally {
      setLoading(false)
      setProgress(0)
    }
  }

  return (
    <>
      <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} loading={loading}>
        Export Excel
      </Button>

      <Modal title="Exporting Data" open={loading} footer={null} closable={false} centered>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Progress type="circle" percent={progress} />
          <p style={{ marginTop: 16 }}>Please wait while exporting data...</p>
        </div>
      </Modal>
    </>
  )
}

export default ButtonExportUserMeyReal
